/**
 * feat-300 — footprint #1 in `QuestionCard` (design NOTES §2, §3.1, §3.2).
 *
 * The `Ask AI` + "type instead" pair, top-right of the stem line, hidden at rest
 * and revealed by hover / focus-within (always shown on touch) — OR, while a
 * thread is open, that thread's {@link ThreadChip}, which stays visible.
 *
 * Renders nothing on a translated column (AT-12) and nothing without a
 * `QuestionEditProvider` (so a bare `QuestionCard` is unchanged).
 */
import React from 'react';
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  FileDiff,
  Keyboard,
  Loader2,
  MessageCircleQuestion,
  Mic,
} from 'lucide-react';
import { QuizQuestion } from '../../../../services/quizContentAdminService';
import { QuestionEditThread } from '../../../../services/questionEditService';
import { SaveSpinner } from '../../curriculum/SaveStatus';
import { Column } from '../shared';
import { PanelIntent, useQuestionEdit } from './QuestionEditProvider';
import {
  AFFORD,
  ASK_BTN,
  TCHIP,
  TCHIP_FAILED,
  TCHIP_NEEDS,
  TCHIP_WORKING,
  TYPE_BTN,
} from './classes';
import { failureSentence, QUESTION_EDIT_COPY as COPY } from './voiceEditCopy';

export const panelDomId = (questionId: string): string => `qe-panel-${questionId}`;
/** Marks the element focus returns to on Hide (the chip, else Ask AI). */
export const TRIGGER_ATTR = 'data-qe-trigger';

/** Focus the card's trigger after the next commit (Hide, Cancel). */
export function focusTrigger(questionId: string): void {
  window.setTimeout(() => {
    const nodes = document.querySelectorAll<HTMLElement>(`[${TRIGGER_ATTR}]`);
    for (let i = 0; i < nodes.length; i += 1) {
      if (nodes[i].getAttribute(TRIGGER_ATTR) === questionId) {
        nodes[i].focus();
        return;
      }
    }
  }, 0);
}

/** The working spinner: `SaveSpinner`, or a static `Loader2` under reduced motion. */
export const WorkingSpinner: React.FC<{ size?: number }> = ({ size = 12 }) => (
  <>
    <span className="inline-flex motion-reduce:hidden" aria-hidden>
      <SaveSpinner size={size} />
    </span>
    <Loader2 className="hidden motion-reduce:inline w-3.5 h-3.5" aria-hidden />
  </>
);

export const ThreadChip: React.FC<{
  thread: QuestionEditThread;
  open: boolean;
  onToggle: () => void;
}> = ({ thread, open, onToggle }) => {
  let cls = TCHIP_NEEDS;
  let icon: React.ReactNode;
  let text: string;
  let aria: string;
  if (thread.state === 'working') {
    cls = TCHIP_WORKING;
    icon = <WorkingSpinner />;
    text = COPY.chipWorking;
    aria = COPY.chipAria.working;
  } else if (thread.state === 'needs_reply') {
    icon = <MessageCircleQuestion className="w-3.5 h-3.5" aria-hidden />;
    text = COPY.chipQuestion;
    aria = COPY.chipAria.question;
  } else if (thread.state === 'proposal_ready') {
    icon = <FileDiff className="w-3.5 h-3.5" aria-hidden />;
    text = COPY.chipProposal;
    aria = COPY.chipAria.proposal;
  } else {
    cls = TCHIP_FAILED;
    icon = <AlertTriangle className="w-3.5 h-3.5" aria-hidden />;
    text = COPY.chipFailed;
    aria = COPY.chipAria.failed(failureSentence(thread.failure_reason));
  }
  const Chevron = open ? ChevronUp : ChevronDown;
  return (
    <button
      type="button"
      className={`${TCHIP} ${cls}`}
      aria-expanded={open}
      aria-controls={panelDomId(thread.question_item_id)}
      aria-label={`${aria}. ${COPY.chipToggle(open)}`}
      title={aria}
      data-testid={`qe-chip-${thread.question_item_id}`}
      data-state={thread.state}
      {...{ [TRIGGER_ATTR]: thread.question_item_id }}
      onClick={onToggle}
    >
      {icon}
      {text}
      <Chevron className="w-3 h-3 opacity-70" aria-hidden />
    </button>
  );
};

const MicButton: React.FC<{ q: QuizQuestion; index: number; column: Column }> = ({
  q,
  index,
  column,
}) => {
  const ctx = useQuestionEdit();
  if (!ctx || column !== null) return null;
  const open = ctx.isPanelOpen(q.id);
  const thread = ctx.openThreadFor(q.id);

  const openWith = (intent: PanelIntent) => {
    ctx.setIntent(q.id, intent);
    ctx.setPanelOpen(q.id, true);
    ctx.requestFocus(q.id);
  };

  if (thread) {
    return (
      <ThreadChip
        thread={thread}
        open={open}
        onToggle={() => (open ? ctx.setPanelOpen(q.id, false) : openWith('show'))}
      />
    );
  }
  return (
    <div className={AFFORD}>
      <button
        type="button"
        className={ASK_BTN}
        aria-label={COPY.askAiAria(index)}
        title={COPY.askAiTitle}
        // NOTES §3.1: `aria-expanded="true"` while its panel is open. Omitted (not
        // "false") at rest: at rest it is an action button, not a disclosure.
        aria-expanded={open ? true : undefined}
        aria-controls={open ? panelDomId(q.id) : undefined}
        {...{ [TRIGGER_ATTR]: q.id }}
        // Already open: focus the panel, never a second recording (NOTES §3.1).
        onClick={() => openWith(open ? 'show' : 'voice')}
      >
        <Mic className="w-3.5 h-3.5" aria-hidden />
        {COPY.askAi}
      </button>
      <button
        type="button"
        className={TYPE_BTN}
        aria-label={COPY.typeAria(index)}
        title={COPY.typeTitle}
        onClick={() => openWith('type')}
      >
        <Keyboard className="w-3.5 h-3.5" aria-hidden />
      </button>
    </div>
  );
};

export default MicButton;
